import { useCallback, useEffect, useRef, useState } from "react";
import { getAssignedDbs } from "@services/api";
import { ROUTING_MAIN_TYPE, USE_FIXTURES } from "../constants";
import { getAssignedDbsState } from "../__fixtures__/fixtureStore";

// Owns the /get-assigned-dbs payload + reload on demand.
//
// Web behavior we mirror:
// - On mount, fetch ?type=TODAY (UPCOMING is commented out on web).
// - After every successful write (assign / unassign / misroute), the caller
//   invokes `reload()`.
// - Errors surface to caller via `error`; UI renders a toast.

const useRoutingData = (type = ROUTING_MAIN_TYPE) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (USE_FIXTURES) {
      if (isMounted.current) {
        // Read mutable state so post-write reloads show the latest payload.
        setData({ ...getAssignedDbsState() });
        setLoading(false);
      }
      return;
    }
    try {
      const res = await getAssignedDbs(type);
      // Server responds { status: 'success', data: {...} }. Defensive against
      // older flat shape just in case.
      const payload = res?.data?.data || res?.data || {};
      if (isMounted.current) {
        setData(payload);
      }
    } catch (e) {
      if (isMounted.current) {
        setError(e);
        setData(null);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [type]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, loading, error, reload };
};

export default useRoutingData;
